/**
 * v1-base — the URL BOUNDARY between the primary application and the legacy one.
 *
 * The redesign is now mounted at the application ROOT (`/home`, `/care`, …) and the
 * legacy portal lives beneath `/v1` (see `app.routes.ts`). That inverts the question
 * every url-scoping predicate used to ask: it is no longer "is this url under `/v2`?"
 * but "is this url NOT legacy?" — because the primary app owns the root and the `**`
 * catch-all, so anything that isn't legacy belongs to it.
 *
 * This module is the single place that answers it. It is deliberately a LEAF (no
 * imports at all, no component references) so both the shared app root
 * (`app.routes.ts` / `app.routes.server.ts`) and the redesign's own predicates can
 * depend on it without pulling a route table or a feature chunk along.
 *
 * Pure + SSR-safe: string work only.
 */

/** The mount segment for the whole legacy application (`/v1`). */
export const V1_BASE = 'v1';

/** `/v1` — the legacy application root (its own former `/` home). */
export const V1_ROOT = `/${V1_BASE}`;

/**
 * The legacy application's former TOP-LEVEL url segments, which still resolve at the
 * root as compatibility redirects into `/v1/...` (see `legacyRootRedirects` in
 * `app.routes.ts`).
 *
 * They exist for two reasons, and both are load-bearing:
 *   · the legacy app navigates to its own screens with ABSOLUTE paths
 *     (`router.navigate(['/articles'])`, `routerLink="/settings"`, …) in ~55 places.
 *     Redirecting the bare root path is what lets that code keep working untouched —
 *     which is the whole reason no legacy component had to be rewritten.
 *   · these urls are live bookmarks and indexed pages. A redirect preserves them.
 *
 * `payments` is deliberately ABSENT: the primary app owns `/payments/status` (the Tap
 * card-charge callback), and a blanket `payments` redirect would capture it. The
 * legacy payments subtree is nothing BUT that status dispatcher, which the redesign
 * has replaced, so it has no root path worth preserving — it stays reachable at
 * `/v1/payments/status`.
 */
export const LEGACY_ROOT_SEGMENTS = [
  'appointments',
  'articles',
  'book-appointment',
  'khawiik',
  'mental-health-scales',
  'podcasts',
  'settings',
  'support-groups',
  'talbinah-community',
  'therapeutic-programs',
  'urgent-appointment',
] as const;

/**
 * Root-level segments that are SHARED utility pages rather than part of either app,
 * and which render inside legacy chrome. Kept at the root (not moved under `/v1`)
 * because legacy services navigate to them absolutely — `TranslationFacade` and
 * `AppLanguageService` both `router.navigate(['/clear-cache'])`.
 */
export const SHARED_ROOT_SEGMENTS = ['clear-cache'] as const;

/** Build an absolute `/v1/...` url from segments. `v1Url()` → `/v1`. */
export function v1Url(...segments: readonly string[]): string {
  return segments.length ? `${V1_ROOT}/${segments.join('/')}` : V1_ROOT;
}

/**
 * Is this url/path rendered by the LEGACY application?
 *
 * True for the `/v1` subtree (segment-exact — `/v1` and `/v1/articles` are in, `/v1abc`
 * is not), for a legacy root compat path that is about to redirect there, and for a
 * shared root utility page.
 *
 * The compat paths MUST count: on a direct hit to `/articles` the app root classifies
 * the visit from `location.pathname` in its CONSTRUCTOR — before the router has
 * resolved the redirect — so judging that url as "not legacy" would render the bare
 * redesign outlet for a frame before the legacy page appears.
 *
 * Tolerant of a query string, a fragment and a trailing slash, so it can be handed a
 * raw `location.pathname`, a `Router.url`, or a full `urlAfterRedirects`.
 */
export function isV1Url(url: string | null | undefined): boolean {
  if (!url) return false;
  const path = url.split(/[?#]/)[0]!.replace(/\/+$/, '').toLowerCase();
  if (path === V1_ROOT || path.startsWith(`${V1_ROOT}/`)) return true;

  const first = path.split('/').filter(Boolean)[0] ?? '';
  if (!first) return false; // '/' is the redesign home
  return (
    (LEGACY_ROOT_SEGMENTS as readonly string[]).includes(first) ||
    (SHARED_ROOT_SEGMENTS as readonly string[]).includes(first)
  );
}
